import { useEffect } from "react";

interface CameraVideoModalProps {
  /** 선택된 카메라 ID (예: "V2"). */
  cameraId: string;
  /** 카메라가 속한 구역명 (부제 표시용). */
  zoneName: string;
  onClose: () => void;
}

/**
 * 카메라 영상 뷰어 모달.
 *
 * <p>Stage 0 PoC — 실제 영상 스트림은 아직 연결되지 않았다. 지금은 16:9 영상 영역에
 * "영상 준비 중" placeholder 를 띄운다. 추후 스트림 연동 시 아래 표시된 ▼교체 지점▼ 의
 * placeholder 블록을 {@code <video>} / {@code <img>}(MJPEG) / WebRTC 플레이어로 바꾸면 된다.</p>
 */
export default function CameraVideoModal({
  cameraId,
  zoneName,
  onClose,
}: CameraVideoModalProps) {
  // ESC 로 닫기.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`카메라 ${cameraId} 영상`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-surface-border bg-surface shadow-xl dark:border-surface-darkBorder dark:bg-surface-darkSubtle"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-3 dark:border-surface-darkBorder">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">
              카메라 {cameraId}
            </span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              · {zoneName} 구역
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {/* ▼ 교체 지점 ▼ — 실제 스트림 연동 시 이 16:9 placeholder 를 플레이어로 교체 */}
        <div className="aspect-video w-full bg-slate-900">
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-slate-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-12 w-12"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
            <div className="text-center">
              <p className="text-sm font-medium text-slate-300">영상 준비 중</p>
              <p className="mt-0.5 text-xs text-slate-500">
                스트림이 아직 연결되지 않았습니다 (연동 예정)
              </p>
            </div>
          </div>
        </div>
        {/* ▲ 교체 지점 ▲ */}

        {/* 푸터 */}
        <div className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400">
          카메라 <span className="font-mono">{cameraId}</span> · {zoneName} 구역의
          실시간 영상이 이 자리에 표시됩니다.
        </div>
      </div>
    </div>
  );
}
